import { SidebarEventTypes } from '~/src/dash/analytics/analytics';
import { EBool } from '~/src/dash/api/types/bool';
import { TKey } from '~/src/dash/publicaUI';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import {
  RouteAudienceManagerSettingsList,
  RouteBidderSettingsList,
  RouteBrandSafetyList,
  RouteBrandsDashboard,
  RouteCampaignSettingsList,
  RouteChannelsView,
  RouteConfigurations,
  RouteContextualSegmentSettingsList,
  RouteCreativesLibraryList,
  RouteDashboards,
  RouteDealList,
  RouteForecasting,
  RouteFreewheelSiteList,
  RouteIABCategoryManager,
  RouteInventoryForecast,
  RouteInventoryRouteList,
  RouteInventoryRules,
  RouteMeasurementDashboard,
  RouteMeasurementDrill,
  RouteMonitorList,
  RouteNetworkDashboard,
  RouteNetworkList,
  RouteOrderList,
  RoutePlatformConnects,
  RouteReportsList,
  RouteResellerManagerList,
  RouteSandbox,
  RouteAdVisor,
  RouteSSPReportList,
  RouteTargetingList,
  RouteInventoryRulesList,
  RouteAdBreaksList,
  RouteCustomDataList,
  RouteContentMetadataList,
} from '~/src/dash/router/routes';
import { PID } from '~/src/dash/utils/publishers';
import {
  allowInventoryRuleVersion,
  isAudienceRouteDisabled,
  isCampaignRouteDisabled,
  isDealRouteDisabled,
  isForecastingAllowed,
  isPlatformConnectDisabled,
  shouldDisplayInventoryRules,
} from '~/src/dash/utils/ui';
import { IRoute } from '~/src/dash/views/navbar/NavBar/common/types';

export type NavBarItemLabel =
  // main
  | 'Dashboard'
  | 'Dashboards'
  | 'Reporting'
  | 'Reports Dashboard'
  | 'Report Builder'
  // demand
  | 'Bidders'
  | 'Campaigns'
  | 'Deals'
  // inventory
  | 'Channels'
  | 'Routes'
  | 'Segments'
  | 'Platform Connect'
  // misc
  | 'Brand Safety'
  | 'Measurement'
  | 'Brands Dashboard'
  | 'Ad Insertion'
  | 'Tools'
  | 'Configurations'
  | 'References'
  | 'Glossary'
  | 'Help'
  // admin
  | 'SSP Analytics'
  | 'IAB Category'
  | 'Reseller'
  | 'Freewheel Sites'
  | 'Sandbox'
  | 'AdVisor'
  // super user only testing
  | 'Forecast'
  | 'Inventory Rules'
  | 'Ad Breaks'
  | 'Content Metadata'
  | 'Custom Data';

export const NavBarItemLabels: Record<string, NavBarItemLabel> = {
  // main
  DASHBOARD: 'Dashboard',
  DASHBOARDS: 'Dashboards',
  REPORTING: 'Reporting',
  REPORTSDASHBOARD: 'Reports Dashboard',
  REPORTBUILDER: 'Report Builder',
  // demand
  BIDDERS: 'Bidders',
  CAMPAIGNS: 'Campaigns',
  DEALS: 'Deals',
  // inventory
  CHANNELS: 'Channels',
  ROUTES: 'Routes',
  SEGMENTS: 'Segments',
  PLATFORM_CONNECT: 'Platform Connect',
  // misc
  BRAND_SAFETY: 'Brand Safety',
  MEASUREMENT: 'Measurement',
  BRANDS_DASHBOARD: 'Brands Dashboard',
  AD_INSERTION: 'Ad Insertion',
  TOOLS: 'Tools',
  CONFIGURATIONS: 'Configurations',
  REFERENCES: 'References',
  GLOSSARY: 'Glossary',
  HELP: 'Help',
  // admin
  SSP_ANALYTICS: 'SSP Analytics',
  IAB_CATEGORY: 'IAB Category',
  RESELLER: 'Reseller',
  FREEWHEEL_SITES: 'Freewheel Sites',
  SANDBOX: 'Sandbox',
  ADVISOR: 'AdVisor',
  // super user only testing
  FORECAST: 'Forecast',
  INVENTORY_RULES: 'Inventory Rules',
  AD_BREAKS: 'Ad Breaks',
  CONTENT_METADATA: 'Content Metadata',
  CUSTOM_DATA: 'Custom Data',
};

export interface IPermissionParams {
  user: IUserState;
  session: ISessionState;
  isDemoUser: boolean;
  isSuperUser: boolean;
  isMeasurementEnabled: boolean | number;
}

export interface INavBarSingleRow {
  label: string;
  iconPath: string;
  iconPathDark: string;
  eventName: SidebarEventTypes;
  route?: IRoute;
  permChecks?: string[];
  isSubMenu?: boolean | undefined;
  subViews?: string[] | undefined;
  subItems?: INavBarSubItem[];
  isSuperUserOnly?: boolean;
  isExternalRoute?: boolean;
  isBeta?: boolean;
  // this function should return true if the item should be hidden
  checkIfHidden?: (permissionParams: IPermissionParams) => boolean;
  // this function should return true if the label should have an asterisk because its only available to super user (but not the publisher)
  checkIfAddSuperUserAsteriskToLabel?: (permissionParams: IPermissionParams) => boolean;
  customActiveCheck?: (pathname: string) => boolean;
  tKey?: TKey | ((permissionParams: IPermissionParams) => TKey);
  uiFriendlyTKey?: TKey; // used if a string needs to be modified to better fit the ui
}

export interface INavBarSubItem {
  label: string;
  tKey?: TKey;
  uiFriendlyTKey?: TKey; // used if a string needs to be modified to better fit the ui
  eventName: SidebarEventTypes;
  route: IRoute;
  permChecks?: string[];
  pathString: string;
  customActiveCheck?: (pathname: string) => boolean;
  checkIfHidden?: (permissionParams: IPermissionParams) => boolean;
  isBeta?: boolean;
}
export interface INavBarConfig {
  sections: {
    [key: string]: {
      label: string;
      tKey?: TKey;
      isLabelDisplayed: boolean;
      items: INavBarSingleRow[];
    };
  };
}

export const navBarConfig = {
  sections: {
    Main: {
      label: 'Main',
      isLabelDisplayed: false,
      items: [
        {
          label: NavBarItemLabels.DASHBOARDS,
          tKey: 'Dashboards',
          iconPath: '/assets/shared/svg/navbar/Dashboards16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Dashboards16.svg',
          eventName: 'Sidebar - Home',
          route: RouteDashboards,
        },
        {
          label: NavBarItemLabels.REPORTING,
          tKey: 'Reporting',
          iconPath: '/assets/shared/svg/navbar/ReportingNew16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/ReportingNew16.svg',
          eventName: 'Sidebar - Reporting',
          route: RouteReportsList,
          permChecks: ['read_report', 'write_report'],
        },
      ] as INavBarSingleRow[],
    },
    Demand: {
      label: 'Demand',
      tKey: 'Demand',
      isLabelDisplayed: true,
      items: [
        {
          label: NavBarItemLabels.BIDDERS,
          tKey: 'Bidders',
          iconPath: '/assets/shared/svg/navbar/Bidders16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Bidders16.svg',
          eventName: 'Sidebar - Bidders',
          route: RouteBidderSettingsList,
          permChecks: ['read_bidder', 'write_bidder'],
        },
        {
          isSubMenu: true,
          label: NavBarItemLabels.CAMPAIGNS,
          tKey: 'Campaigns',
          iconPath: '/assets/shared/svg/navbar/Campaigns16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Campaigns16.svg',
          permChecks: ['read_campaign', 'write_campaign'],
          subViews: ['orders', 'line-items', 'creative-library'],
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return isCampaignRouteDisabled(permissionParams.session.publisher);
          },
          subItems: [
            {
              label: 'Orders',
              tKey: 'Orders',
              eventName: 'Sidebar - Orders',
              route: RouteOrderList,
              pathString: 'orders',
              customActiveCheck: (pathname) =>
                pathname.indexOf('orders') > -1 && !(pathname.indexOf('line-items') > -1),
            },
            {
              label: 'Line Items',
              tKey: 'LineItems',
              eventName: 'Sidebar - Line Items',
              route: RouteCampaignSettingsList,
              pathString: 'line-items',
            },
            {
              label: 'Creative Library',
              tKey: 'CreativeLibrary',
              uiFriendlyTKey: 'UICreativeLibrary',
              eventName: 'Sidebar - Creative Library',
              route: RouteCreativesLibraryList,
              pathString: 'creative',
            },
          ],
        },
        {
          label: NavBarItemLabels.DEALS,
          tKey: 'Deals',
          iconPath: '/assets/shared/svg/navbar/Deals16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Deals16.svg',
          eventName: 'Sidebar - Deals',
          route: RouteDealList,
          permChecks: ['read_deal', 'write_deal'],
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return isDealRouteDisabled(permissionParams.session.publisher);
          },
        },
        {
          label: NavBarItemLabels.PLATFORM_CONNECT,
          tKey: 'PlatformConnect',
          uiFriendlyTKey: 'UIPlatformConnect',
          iconPath: '/assets/shared/svg/ConnectWithoutContact.svg',
          iconPathDark: '/assets/shared/svg/ConnectWithoutContact.svg',
          eventName: 'Sidebar - Platform Connect',
          route: RoutePlatformConnects,
          checkIfHidden: (permissionParams: IPermissionParams) => {
            if (permissionParams.isSuperUser) {
              return false;
            }
            return isPlatformConnectDisabled(permissionParams.session.publisher);
          },
          checkIfAddSuperUserAsteriskToLabel: (permissionParams: IPermissionParams) => {
            return (
              isPlatformConnectDisabled(permissionParams.session.publisher) &&
              permissionParams.isSuperUser
            );
          },
        },
      ] as INavBarSingleRow[],
    },
    Inventory: {
      label: 'Inventory',
      tKey: 'Inventory',
      isLabelDisplayed: true,
      items: [
        {
          label: NavBarItemLabels.CHANNELS,
          tKey: 'Channels',
          iconPath: '/assets/shared/svg/navbar/Channels16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Channels16.svg',
          eventName: 'Sidebar - Channels',
          route: RouteChannelsView,
          permChecks: ['read_channel', 'write_channel'],
        },
        {
          label: NavBarItemLabels.INVENTORY_RULES,
          tKey: (permissionParams: IPermissionParams) => {
            return allowInventoryRuleVersion(permissionParams.session.publisher, 2) &&
              permissionParams.isSuperUser
              ? 'InventoryRulesV1WithAsterisk'
              : 'InventoryRules';
          },
          iconPath: '/assets/shared/svg/navbar/InventoryRules16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/InventoryRulesDark16.svg',
          eventName: 'Sidebar - Inventory Rules',
          route: RouteInventoryRules,
          customActiveCheck: (pathname) => pathname.endsWith('inventory-rules'),
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return !(
              (allowInventoryRuleVersion(permissionParams.session.publisher, 1) &&
                shouldDisplayInventoryRules(permissionParams.session.publisher)) ||
              permissionParams.isSuperUser
            );
          },
        },
        {
          isSubMenu: true,
          label: NavBarItemLabels.INVENTORY_RULES,
          tKey: (permissionParams: IPermissionParams) => {
            return allowInventoryRuleVersion(permissionParams.session.publisher, 2) &&
              (shouldDisplayInventoryRules(permissionParams.session.publisher) ||
                permissionParams.isSuperUser)
              ? 'InventoryRules'
              : 'InventoryRulesV2WithAsterisk';
          },
          iconPath: '/assets/shared/svg/navbar/InventoryRules16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/InventoryRulesDark16.svg',
          subViews: ['/rules', 'ad-breaks', 'content-metadata', 'custom-data'],
          subItems: [
            {
              label: 'Rules',
              tKey: 'Rules',
              eventName: 'Sidebar - Rules',
              route: RouteInventoryRulesList,
              pathString: 'rules',
              customActiveCheck: (pathname) => pathname.endsWith('inventory/rules'),
            },
            {
              label: NavBarItemLabels.AD_BREAKS,
              tKey: 'AdBreaks',
              eventName: 'Sidebar - Ad Breaks',
              route: RouteAdBreaksList,
              pathString: 'ad-breaks',
              customActiveCheck: (pathname) =>
                pathname.indexOf('ad-breaks') > -1 && pathname.indexOf('ad-breaks') > -1,
            },
            {
              label: NavBarItemLabels.CONTENT_METADATA,
              tKey: 'ContentMetadata',
              eventName: 'Sidebar - Content Metadata',
              route: RouteContentMetadataList,
              pathString: 'content-metadata',
              customActiveCheck: (pathname) =>
                pathname.indexOf('content-metadata') > -1 &&
                pathname.indexOf('content-metadata') > -1,
            },
            {
              label: NavBarItemLabels.CUSTOM_DATA,
              tKey: 'CustomData',
              eventName: 'Sidebar - Custom Data',
              route: RouteCustomDataList,
              pathString: 'custom-data',
              customActiveCheck: (pathname) =>
                pathname.indexOf('custom-data') > -1 && pathname.indexOf('custom-data') > -1,
            },
          ],
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return !(
              (allowInventoryRuleVersion(permissionParams.session.publisher, 2) ||
                [
                  PID.publica,
                  PID.kennyTesting,
                  PID.qaPublica,
                  PID.sonyProd,
                  PID.sonyStage,
                  PID.sonyUAT,
                ].includes(permissionParams.session.publisher.ID)) &&
              (shouldDisplayInventoryRules(permissionParams.session.publisher) ||
                permissionParams.isSuperUser)
            );
          },
        },
        {
          label: NavBarItemLabels.ROUTES,
          tKey: 'Routes',
          iconPath: '/assets/shared/svg/route.svg',
          iconPathDark: '/assets/shared/svg/route_black.svg',
          eventName: 'Sidebar - Routes',
          route: RouteInventoryRouteList,
        },
        {
          isSubMenu: true,
          label: NavBarItemLabels.SEGMENTS,
          tKey: 'Segments',
          iconPath: '/assets/shared/svg/navbar/Segments16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Segments16.svg',
          subViews: ['audience', 'contextual'],
          subItems: [
            {
              label: 'Audience',
              tKey: 'Audience',
              eventName: 'Sidebar - Audience Segments',
              route: RouteAudienceManagerSettingsList,
              permChecks: ['read_audience_manager', 'write_audience_manager'],
              pathString: 'audience',
              customActiveCheck: (pathname) =>
                pathname.indexOf('segments') > -1 && pathname.indexOf('audience') > -1,
            },
            {
              label: 'Contextual',
              tKey: 'Contextual',
              eventName: 'Sidebar - Contextual Segments',
              route: RouteContextualSegmentSettingsList,
              pathString: 'contextual',
              customActiveCheck: (pathname) =>
                pathname.indexOf('segments') > -1 && pathname.indexOf('contextual') > -1,
            },
          ],
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return isAudienceRouteDisabled(permissionParams.session.publisher);
          },
        },
      ] as INavBarSingleRow[],
    },
    Miscellaneous: {
      label: 'Miscellaneous',
      tKey: 'Miscellaneous',
      isLabelDisplayed: false,
      items: [
        {
          label: NavBarItemLabels.BRAND_SAFETY,
          tKey: 'BrandSafety',
          iconPath: '/assets/shared/svg/navbar/BrandSafety16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/BrandSafety16.svg',
          eventName: 'Sidebar - Brand Safety',
          route: RouteBrandSafetyList,
          permChecks: ['read_channel_brandsafety_rule', 'write_channel_brandsafety_rule'],
        },
        {
          isSubMenu: true,
          label: NavBarItemLabels.MEASUREMENT,
          tKey: 'Measurement',
          iconPath: '/assets/shared/svg/navbar/Measurement16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Measurement16.svg',
          subViews: ['measurement-dashboard', 'measurement-drill'],
          subItems: [
            {
              label: 'Overview',
              tKey: 'Overview',
              eventName: 'Sidebar - Measurement Dashboard',
              route: RouteMeasurementDashboard,
              pathString: 'measurement-dashboard',
            },
            {
              label: 'Drill',
              tKey: 'Drill',
              eventName: 'Sidebar - Measurement Drill',
              route: RouteMeasurementDrill,
              pathString: 'measurement-drill',
            },
          ],
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return !(permissionParams.isSuperUser || permissionParams.isMeasurementEnabled);
          },
        },
        {
          label: NavBarItemLabels.BRANDS_DASHBOARD,
          tKey: 'BrandsDashboard',
          uiFriendlyTKey: 'UIBrandsDashboard',
          iconPath: '/assets/shared/svg/navbar/BrandsDash16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/BrandsDash16.svg',
          eventName: 'Sidebar - Brands Dashboard',
          route: RouteBrandsDashboard,
        },
        {
          isSubMenu: true,
          label: 'Ad Insertion',
          tKey: 'AdInsertion',
          iconPath: '/assets/shared/svg/navbar/AdInsertion16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/AdInsertion16.svg',
          permChecks: ['read_network', 'write_network'],
          subViews: ['network-dashboard', 'network-list', 'network'],
          subItems: [
            {
              label: 'Dashboard',
              tKey: 'Dashboard',
              eventName: 'Sidebar - SSAI Dashboard',
              route: RouteNetworkDashboard,
              pathString: 'network-dashboard',
            },
            {
              label: 'Networks',
              tKey: 'Networks',
              eventName: 'Sidebar - Network',
              route: RouteNetworkList,
              pathString: 'network-list',
              customActiveCheck: (pathname) =>
                (pathname.indexOf('network-list') > -1 || pathname.indexOf('network') > -1) &&
                !(pathname.indexOf('network-dashboard') > -1),
            },
          ],
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return permissionParams.session.publisher.NetworkEnabled !== EBool.EBool_TRUE;
          },
        },
        {
          isSubMenu: true,
          label: NavBarItemLabels.TOOLS,
          tKey: 'Tools',
          iconPath: '/assets/shared/svg/navbar/Tools16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Tools16.svg',
          subViews: ['inventory-forecast', 'monitor', 'targeting'],
          subItems: [
            {
              label: NavBarItemLabels.FORECAST,
              tKey: 'Forecast',
              eventName: 'Sidebar - Forecast',
              route: RouteForecasting,
              pathString: 'forecasting',
              checkIfHidden: (permissionParams: IPermissionParams) => {
                return !(
                  permissionParams.isSuperUser ||
                  isForecastingAllowed(permissionParams.session.publisher)
                );
              },
            },
            {
              // Previous version of our forecasting tool
              // Is the reverse of the above, where some pubs will have access to the new forecast UI
              // And the rest will have access to this older one here
              label: 'Inventory Forecast',
              tKey: 'InventoryForecast',
              eventName: 'Sidebar - Inventory Forecast',
              route: RouteInventoryForecast,
              pathString: 'inventory-forecast',
              checkIfHidden: (permissionParams: IPermissionParams) => {
                if (permissionParams.isSuperUser) {
                  return false;
                }
                return isForecastingAllowed(permissionParams.session.publisher);
              },
            },
            {
              label: 'Monitors',
              tKey: 'Monitors',
              eventName: 'Sidebar - Monitors',
              route: RouteMonitorList,
              pathString: 'monitor',
            },
            {
              label: 'Targeting Presets',
              tKey: 'TargetingPresets',
              eventName: 'Sidebar - Targeting Presets',
              route: RouteTargetingList,
              pathString: 'targeting',
            },
          ],
        },
        {
          label: NavBarItemLabels.CONFIGURATIONS,
          tKey: 'Configurations',
          iconPath: '/assets/shared/svg/navbar/Configurations16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Configurations16.svg',
          eventName: 'Sidebar - Configurations',
          route: RouteConfigurations,
        },
      ] as INavBarSingleRow[],
    },
    Admin: {
      label: 'Admin',
      isLabelDisplayed: true,
      items: [
        {
          label: NavBarItemLabels.SSP_ANALYTICS,
          iconPath: '/assets/shared/svg/navbar/SSP16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/SSP16.svg',
          eventName: 'Sidebar - SSP Analytics',
          route: RouteSSPReportList,
          isSuperUserOnly: true,
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return !(permissionParams.isSuperUser && permissionParams.session.publisher.ID === '1');
          },
        },
        {
          label: NavBarItemLabels.IAB_CATEGORY,
          iconPath: '/assets/shared/svg/navbar/IABCat16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/IABCat16.svg',
          eventName: 'Sidebar - IAB Category',
          route: RouteIABCategoryManager,
          isSuperUserOnly: true,
          checkIfHidden: (permissionParams: IPermissionParams) => {
            return !permissionParams.isSuperUser;
          },
        },
        {
          label: NavBarItemLabels.RESELLER,
          iconPath: '/assets/shared/svg/reseller.svg',
          iconPathDark: '/assets/shared/svg/reseller_black.svg',
          eventName: 'Sidebar - Reseller',
          route: RouteResellerManagerList,
          isSuperUserOnly: true,
        },
        {
          label: NavBarItemLabels.FREEWHEEL_SITES,
          iconPath: '/assets/shared/svg/reseller.svg',
          iconPathDark: '/assets/shared/svg/reseller_black.svg',
          eventName: 'Sidebar - Freewheel Sites',
          route: RouteFreewheelSiteList,
          isSuperUserOnly: true,
        },
        {
          label: NavBarItemLabels.SANDBOX,
          iconPath: '/assets/shared/svg/navbar/Dashboards16.svg',
          iconPathDark: '/assets/shared/svg/navbar/black/Dashboards16.svg',
          eventName: 'Sidebar - Sandbox',
          route: RouteSandbox,
          isSuperUserOnly: true,
        },
        {
          label: NavBarItemLabels.ADVISOR,
          iconPath: '/assets/shared/svg/navbar/AdVisor.svg',
          iconPathDark: '/assets/shared/svg/navbar/AdVisor.svg',
          eventName: 'Sidebar - AdVisor',
          route: RouteAdVisor,
          isSuperUserOnly: true,
        },
      ] as INavBarSingleRow[],
    },
  },
} as INavBarConfig;
